import {
    AsOfBadge,
    Badge,
    Banner,
    DateTime,
    DetailRow,
    FieldGrid,
    ObjectRef,
    PageHeader,
    PublicId,
    Section,
    StatusBadge,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@bemor/web-kit/ui';
import { useParams } from 'react-router-dom';

import type { StaffUser } from '@/api/types';
import { ActivityCard } from '@/components/activity';
import { Comments } from '@/components/comments';
import { DetailBody } from '@/components/detail';
import { detailTitle } from '@/components/detail-title';
import { LifecycleActions, type LifecycleAction } from '@/lib/lifecycle';
import { P } from '@/lib/permissions';
import { useOne } from '@/lib/queries';

/** The actions a user's state allows (UserAdminService::allows; ImpersonationService::start). */
export function userActions(u: StaffUser): LifecycleAction[] {
    const base = `/staff/users/${u.id}`;
    return [
        {
            key: 'impersonate',
            path: `${base}/impersonate`,
            label: 'Sign in as',
            title: `Sign in as ${u.name}`,
            description:
                'A new tab opens the dashboard as this user. Everything you do is logged under your name.',
            permission: P.usersImpersonate,
            enabled: u.status === 'active',
            opens: true,
        },
        {
            key: 'suspend',
            path: `${base}/suspend`,
            label: 'Suspend',
            title: 'Suspend this user',
            description: 'They are signed out everywhere and cannot sign in.',
            permission: P.usersManage,
            destructive: true,
            enabled: ['active', 'invited', 'pending_verification'].includes(
                u.status,
            ),
        },
        {
            key: 'reactivate',
            path: `${base}/reactivate`,
            label: 'Reactivate',
            title: 'Reactivate this user',
            description: 'They can sign in again.',
            permission: P.usersManage,
            enabled: u.status === 'suspended',
        },
        {
            key: 'totp-reset',
            path: `${base}/totp-reset`,
            label: 'Reset two-factor',
            title: 'Reset two-factor',
            description:
                'Their authenticator and recovery codes are cleared; they enrol again at next sign-in.',
            permission: P.usersManage,
            destructive: true,
            enabled: u.totp_enabled,
        },
        {
            key: 'end-sessions',
            path: `${base}/end-sessions`,
            label: 'End sessions',
            title: 'End every session',
            description: 'Every session and trusted device is revoked.',
            permission: P.usersManage,
            destructive: true,
            enabled: true,
        },
    ];
}

/** BM-93 (design 0.9.0 `backoffice/user`): the user, their organizations and merchants, sessions, devices, notes, activity. */
export function UserPage() {
    const { id = '' } = useParams();
    const query = useOne<StaffUser>(`/staff/users/${id}`);
    const u = query.data?.data;
    return (
        <div>
            <PageHeader
                crumbs={[{ label: 'Users', href: '/users' }]}
                {...detailTitle(query, u?.name, id)}
                meta={
                    <>
                        <PublicId id={id} short={false} />
                        <AsOfBadge asOf={query.freshness.asOf} />
                        {u ? (
                            <StatusBadge status={u.status} domain="user" />
                        ) : null}
                    </>
                }
                secondaryAction={
                    u ? (
                        <LifecycleActions
                            invalidate={[`/staff/users/${id}`, '/staff/users']}
                            actions={userActions(u)}
                        />
                    ) : null
                }
            />
            <DetailBody query={query} title="User">
                {(user) => (
                    <div className="space-y-4">
                        {user.status === 'suspended' ? (
                            <Banner
                                tone="warning"
                                title="Suspended"
                                data-testid="user-suspended"
                            >
                                {user.suspended_at ? (
                                    <DateTime value={user.suspended_at} />
                                ) : null}
                                {user.suspend_reason
                                    ? `: ${user.suspend_reason}`
                                    : null}
                            </Banner>
                        ) : null}
                        <Section title="Account" testId="user-head">
                            <FieldGrid>
                                <DetailRow label="Email" layout="stacked">
                                    {user.email}
                                </DetailRow>
                                <DetailRow label="Two-factor" layout="stacked">
                                    {user.totp_enabled ? 'On' : 'Off'}
                                </DetailRow>
                                <DetailRow label="Password" layout="stacked">
                                    {user.has_password ? 'Set' : 'Google only'}
                                </DetailRow>
                                <DetailRow
                                    label="Email verified"
                                    layout="stacked"
                                >
                                    <DateTime
                                        zone="inline"
                                        value={user.email_verified_at}
                                        empty="not yet"
                                    />
                                </DetailRow>
                                <DetailRow
                                    label="Last sign-in"
                                    layout="stacked"
                                >
                                    <DateTime
                                        zone="inline"
                                        value={user.last_login_at}
                                        empty="never"
                                    />
                                </DetailRow>
                                <DetailRow label="Time zone" layout="stacked">
                                    {user.timezone ?? '—'}
                                </DetailRow>
                            </FieldGrid>
                        </Section>
                        <Section
                            title="Organizations"
                            flush
                            testId="user-organizations"
                        >
                            <ul className="m-0 list-none p-0">
                                {user.organizations.map((o) => (
                                    <li
                                        key={o.id}
                                        className="border-border-1 flex flex-wrap items-center justify-between gap-2 border-t px-5 py-3 first:border-t-0"
                                    >
                                        <ObjectRef
                                            id={o.id}
                                            label={o.name}
                                            href={`/organizations/${o.id}`}
                                        />
                                        <span className="flex items-center gap-2">
                                            <Badge
                                                variant="neutral"
                                                emphasis="outline"
                                                size="sm"
                                            >
                                                {o.role}
                                            </Badge>
                                            <StatusBadge
                                                status={o.status}
                                                domain="member"
                                                size="sm"
                                            />
                                        </span>
                                    </li>
                                ))}
                                {user.organizations.length === 0 ? (
                                    <li className="text-fg-2 px-5 py-4 [font:var(--body-sm)]">
                                        None.
                                    </li>
                                ) : null}
                            </ul>
                        </Section>
                        <Section
                            title="Merchants"
                            flush
                            testId="user-merchants"
                        >
                            <ul className="m-0 list-none p-0">
                                {user.merchants.map((m) => (
                                    <li
                                        key={m.id}
                                        className="border-border-1 flex flex-wrap items-center justify-between gap-2 border-t px-5 py-3 first:border-t-0"
                                    >
                                        <ObjectRef
                                            id={m.id}
                                            label={m.display_name}
                                            href={`/merchants/${m.id}`}
                                        />
                                        <span className="flex items-center gap-2">
                                            <Badge
                                                variant="neutral"
                                                emphasis="outline"
                                                size="sm"
                                            >
                                                {m.role}
                                                {m.implicit
                                                    ? ' (organization)'
                                                    : ''}
                                            </Badge>
                                            <StatusBadge
                                                status={m.status}
                                                domain="merchant"
                                                size="sm"
                                            />
                                        </span>
                                    </li>
                                ))}
                                {user.merchants.length === 0 ? (
                                    <li className="text-fg-2 px-5 py-4 [font:var(--body-sm)]">
                                        None.
                                    </li>
                                ) : null}
                            </ul>
                        </Section>
                        <Section title="Sessions" flush testId="user-sessions">
                            {user.sessions.length === 0 ? (
                                <p className="text-fg-2 m-0 px-5 py-4 [font:var(--body-sm)]">
                                    No active session.
                                </p>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Device</TableHead>
                                            <TableHead>IP</TableHead>
                                            <TableHead>Last seen</TableHead>
                                            <TableHead>Expires</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {user.sessions.map((s) => (
                                            <TableRow
                                                key={s.id}
                                                data-testid={`session-${s.id}`}
                                            >
                                                <TableCell>
                                                    <span className="inline-flex items-center gap-2">
                                                        {s.label ??
                                                            'Unknown device'}
                                                        {s.impersonated_by ? (
                                                            <Badge
                                                                variant="warning"
                                                                emphasis="soft"
                                                                size="sm"
                                                            >
                                                                Impersonation
                                                            </Badge>
                                                        ) : null}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="font-mono text-xs">
                                                    {s.ip ?? '—'}
                                                </TableCell>
                                                <TableCell>
                                                    <DateTime
                                                        value={s.last_seen_at}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <DateTime
                                                        value={s.expires_at}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Section>
                        <Section
                            title="Trusted devices"
                            flush
                            testId="user-devices"
                        >
                            {user.devices.length === 0 ? (
                                <p className="text-fg-2 m-0 px-5 py-4 [font:var(--body-sm)]">
                                    None.
                                </p>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Device</TableHead>
                                            <TableHead>IP</TableHead>
                                            <TableHead>Trusted until</TableHead>
                                            <TableHead>Last used</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {user.devices.map((d) => (
                                            <TableRow key={d.id}>
                                                <TableCell>
                                                    {d.label ??
                                                        'Unknown device'}
                                                </TableCell>
                                                <TableCell className="font-mono text-xs">
                                                    {d.ip ?? '—'}
                                                </TableCell>
                                                <TableCell>
                                                    <DateTime
                                                        value={d.trusted_until}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <DateTime
                                                        value={d.last_used_at}
                                                        empty="never"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Section>
                    </div>
                )}
            </DetailBody>
            <div className="mt-4">
                <Comments subjectType="user" subjectId={id} />
            </div>
            <div className="mt-4">
                <ActivityCard entity="user" id={id} />
            </div>
        </div>
    );
}
